import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useIntl } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { MultiSelect } from "@/components/design/MultiSelect";
import { ResultCard } from "@/components/search/ResultCard";
import { CompanyLogo } from "@/pages/company/CompanyLogo";
import type { CompanyLogoValue } from "@/pages/company/company-logo";
import { useCompanyLogos } from "@/lib/use-company-logos";
import { EMPTY_COMPANY_FILTERS, buildCompanyWhere } from "@/lib/search-filters";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useDebounced } from "@/lib/use-debounced";
import { useGlobalCompanySearch } from "@/lib/use-global-company-search";
import { MATCH_COMPANY_LIMIT } from "./match-constants";
import { useRequirementsReferenceData } from "./use-requirements-reference-data";
import { useMatchCompanyPool, type CompanyPoolRow } from "./use-match-company-pool";
import { AutoMatchRunsBox } from "./auto-match/AutoMatchRunsBox";
import type { UseMatchEditorResult } from "./use-match-editor";

// STEP 2 OF A COMPANY MATCH: the pool of COMPANIES this match will rank.
//
// It is its own screen rather than a mode of PeopleStep because that screen is a person finder that
// pins people as candidates — every control on it (avatars, professions, groups and pins, the lock
// toggle, the "interested people" shortcut) is about people, and routing a company match through it
// would not merely show unused controls but would let a user fill a company match with people.
//
// THE SEARCH IS THE PLATFORM'S COMPANY SEARCH, unchanged: `useGlobalCompanySearch` pages genesis's
// globalSearch over the COMPANY entity type with a structured filter built by `buildCompanyWhere`.
// What is searchable is fixed by the company search (name, expertise labels, industry) and
// lives in genesis's index; nothing here can widen it, and nothing here ranks.
//
// ADDING IS SELECTING, NOT MATCHING. 0005 §3 forbids a match TRIGGER on a search hit — search is
// lexical finding, never matching — and says nothing about adding; Kevin decided adding is allowed.
// So a row's action writes the pool and stays on the page: it never navigates, and it never starts
// a run. The ranking is a separate, explicit step.
//
// THE COLLECT BUTTON TAKES EVERY MATCH, NOT THE LOADED ROWS (spark#293). It used to read the paged
// `search.nodes`, so one click took one page: at 185 companies that was eight "load more" clicks
// before the button did what it said. It now goes through `search.fetchAllIds`, the same one-shot
// read `PeopleStep` has always used, and the label follows the same rule — "up to N" while further
// pages exist, the exact number once everything is in. The difference was never a product decision:
// jfalkenstein, 2026-09-16, "Nein das war nicht abgesprochen."

/**
 * PAGE SIZE, DELIBERATELY LEFT AT 24 (spark#293 step 5).
 *
 * <p><b>It used to decide how much the collect button could take, and that is the only thing that
 * made it worth arguing about.</b> Since the button reads the whole result set instead of the
 * rendered rows, the page size governs one thing alone: how far you scroll before "Mehr laden".
 * Changing it would be a UX change nobody asked for in this ticket, so the number stays and this
 * change moves exactly one behaviour.
 *
 * <p><b>Two arguments that look good and are wrong</b>, recorded because an earlier draft of this
 * note made both. There is no column count for a page size to divide into: the finder list is
 * `grid-cols-1`, one card per row at every width. And `MAX_HYDRATED_IDS` (50) in
 * use-global-company-search is not a ceiling either — a page above it simply hydrates in more than
 * one round, because `pending` recomputes from what is still missing as soon as the previous round
 * lands, which is what the comment on that constant already says.
 */
const PAGE_SIZE = 24;

export function CompanyPoolStep({ editor }: { editor: UseMatchEditorResult }) {
  const intl = useIntl();
  const { industries } = useRequirementsReferenceData();
  const [query, setQuery] = useState("");
  const [industryIds, setIndustryIds] = useState<string[]>([]);
  /**
   * What to say after a bulk add, or null to say nothing. Two different facts, so two fields rather
   * than one number — the same shape `PeopleStep.sampleNotice` uses:
   * `added` is how many actually went in, and `capped` marks the case where the SEARCH had more
   * matches than {@link MATCH_COMPANY_LIMIT} could carry, which only became reachable once the
   * button stopped being bounded by the rendered page (spark#293).
   */
  const [addNotice, setAddNotice] = useState<{ added: number; capped: boolean } | null>(null);
  /**
   * The collect button's own failure, which nothing else can report.
   *
   * <p>Pool WRITES already speak through `pool.refusal`, but since spark#293 the button also makes
   * a READ of its own — and that read sits outside the pool hook, so a rejection had no way to
   * reach the screen. A click that silently does nothing is the worst of the three outcomes: the
   * user cannot tell it from "there was nothing to add" or from a broken button.
   */
  const [collectFailed, setCollectFailed] = useState(false);

  const canEdit = editor.canEditPool;
  const pool = useMatchCompanyPool({
    matchId: editor.matchId,
    canEdit,
    // A pool change invalidates a ranking computed from the old pool — the same reason the person
    // path clears the solver result when the candidate set moves.
    onPoolChanged: editor.clearSolverResult,
  });

  const debouncedQuery = useDebounced(query, 250);
  const debouncedIndustryIds = useDebounced(industryIds, 250);

  // THE BRANCHE PRE-FILTER, which is the same Branche as the criterion (Kevin: it is both). Built
  // through `buildCompanyWhere` rather than hand-written, so this filter is the same
  // CompanyFilterInput the extended search sends — genesis's CompanyFilterSpec matches ONE criterion
  // per filter object, and hand-rolling the shape here is how a second criterion would come to be
  // silently dropped.
  const companyFilter = useMemo(
    () => buildCompanyWhere({ ...EMPTY_COMPANY_FILTERS, industryIds: debouncedIndustryIds }),
    [debouncedIndustryIds],
  );

  const search = useGlobalCompanySearch({
    query: debouncedQuery,
    companyFilter,
    pageSize: PAGE_SIZE,
  });

  // The industries the match's own criteria name. The pre-filter is NOT seeded from them
  // automatically: narrowing a search without being asked hides companies the user was looking for,
  // and a criterion may deliberately be a Kann. Offered as one click instead.
  const criteriaIndustryIds = useMemo(() => {
    const ids = (editor.data.companyConditions ?? [])
      .filter((c) => c.__typename === "CompanyIndustryCondition")
      .map((c) => c.industryId)
      .filter((id): id is string => Boolean(id));
    return [...new Set(ids)];
  }, [editor.data.companyConditions]);
  const canTakeOverIndustries =
    criteriaIndustryIds.length > 0 &&
    criteriaIndustryIds.some((id) => !industryIds.includes(id));

  const industryLabel = useMemo(() => {
    const byId = new Map(industries.map((i) => [i.value, i.label]));
    return (id: string | null) =>
      id
        ? (byId.get(id) ??
          intl.formatMessage({ id: "match.companyPool.unknownIndustry", defaultMessage: "Branche unbekannt" }))
        : intl.formatMessage({ id: "match.companyPool.noIndustry", defaultMessage: "keine Branche" });
  }, [industries, intl]);

  const criteriaCount =
    (editor.data.companyConditions ?? []).length + (editor.data.unknownCompanyConditions ?? []).length;

  // View state only, never the rows themselves (@external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): what the assistant may know about this
  // screen is how big the pool is and how the search is narrowed.
  useAssistantContext([
    {
      description: "the company pool the user is filling",
      value:
        `${pool.total} companies in the pool; ${criteriaCount} company criteria` +
        (debouncedQuery.trim() ? `; search "${debouncedQuery.trim()}"` : "") +
        (debouncedIndustryIds.length ? `; ${debouncedIndustryIds.length} industry pre-filters` : ""),
    },
  ]);

  const found = search.nodes;
  const notInPool = useMemo(
    () => found.filter((hit) => !pool.byCompanyId.has(hit.id)),
    [found, pool.byCompanyId],
  );
  // Room left before genesis refuses (`TOO_MANY_MATCH_COMPANIES`). The bulk add is trimmed to it
  // rather than sent whole: genesis counts the REQUEST against the cap, so an over-long batch is
  // refused in full and would add nothing at all.
  const room = Math.max(0, MATCH_COMPANY_LIMIT - pool.total);

  // WHETHER THE BUTTON CAN DO ANYTHING, which is no longer "are there loaded rows left to take".
  // While further pages exist the unloaded ones may or may not already be in the pool, so the
  // honest test is that the search found something and the pool has room; the click then filters
  // against the pool with the full set in hand. Once everything is loaded the remainder IS known,
  // and an empty one disables the button as before.
  const canBulkAdd = canEdit && room > 0 && (search.hasMore ? found.length > 0 : notInPool.length > 0);

  // THE LOGOS ARE HYDRATED, not selected on the pool query. `matchCompanies` asks for the whole pool
  // in one page (first: MATCH_COMPANY_LIMIT), so a `logo` field there would ship one base64 image per
  // pinned company in a single response — the exact case genesis' four-surface rule for
  // `Company.logo` exists to prevent. `companyIdentities` is the surface built for this shape and is
  // server-capped, so the ask is bounded per round; see use-company-logos.
  const poolCompanyIds = useMemo(() => pool.rows.map((row) => row.companyId), [pool.rows]);
  const { logoOf } = useCompanyLogos(poolCompanyIds);

  const anyFilterActive = query.trim().length > 0 || industryIds.length > 0;

  async function addAllFound() {
    if (!canBulkAdd) return;
    setAddNotice(null);
    setCollectFailed(false);

    // THE WHOLE RESULT SET, not the rendered rows — spark#293. `capped` says the search matched
    // more than the pool's ceiling could ever carry, which is a different sentence from "the pool
    // filled up", and only reachable now that the ask is no longer bounded by one page.
    //
    // CAUGHT, because `onClick` discards this promise: an uncaught rejection here is an unhandled
    // rejection in the console and absolutely nothing on screen. The pool's own writes are already
    // wrapped this way inside `useMatchCompanyPool.write`; this read is the one call on the path
    // that is not.
    let ids: readonly string[];
    let capped: boolean;
    try {
      ({ ids, capped } = await search.fetchAllIds(MATCH_COMPANY_LIMIT));
    } catch {
      setCollectFailed(true);
      return;
    }

    // FILTER FIRST, TRUNCATE SECOND, and the order is the whole point: genesis counts the REQUEST
    // against MAX_MATCH_COMPANIES and refuses an over-long batch in full, so truncating before
    // dropping the companies already in the pool would spend the budget on rows that are no-ops
    // and silently leave real ones out.
    const addable = ids.filter((id) => !pool.byCompanyId.has(id));
    const toAdd = addable.slice(0, room);

    if (toAdd.length === 0) {
      // Nothing to write, but there is still something to say when the search itself overflowed.
      setAddNotice(capped ? { added: 0, capped: true } : null);
      return;
    }

    // The notice is set from the ANSWER, not from the intent: it states how many companies were
    // taken in, so a refused write must not leave a sentence claiming it happened.
    const added = await pool.addMany(toAdd);
    const trimmed = toAdd.length < addable.length;
    setAddNotice(added && (trimmed || capped) ? { added: toAdd.length, capped } : null);
  }

  async function addOne(companyId: string) {
    setAddNotice(null);
    setCollectFailed(false);
    await pool.add(companyId);
  }

  function resetFilters() {
    setQuery("");
    setIndustryIds([]);
  }

  return (
    <div>
      <p className="max-w-xl text-smycles-body text-ink-muted">
        {intl.formatMessage({
          id: "match.companyPool.intro",
          defaultMessage:
            "Suche Unternehmen und nimm sie in den Pool dieses Matches auf. Bewertet wird jedes Unternehmen einzeln gegen die Kriterien aus dem Schritt Challenge.",
        })}
      </p>
      {criteriaCount === 0 ? (
        // Neutral and textual: an empty criteria list is a state of the match, not an error. genesis
        // ranks such a pool without complaint — every company carries the same empty evidence — so
        // the sentence says what the outcome will be rather than blocking anything.
        <p className="smycles-field-meta mt-2 max-w-xl text-ink-muted">
          {intl.formatMessage({
            id: "match.companyPool.noCriteriaYet",
            defaultMessage:
              "Für dieses Match sind noch keine Unternehmens-Anforderungen gesetzt. Die Rangliste entsteht dann ohne Nachweis.",
          })}
        </p>
      ) : null}
      {!canEdit ? (
        <p className="smycles-field-meta mt-2 max-w-xl text-ink-muted" role="status">
          {intl.formatMessage({
            id: "match.companyPool.readOnly",
            defaultMessage:
              "Du kannst diesen Pool nur ansehen. Zum Aufnehmen und Entfernen von Unternehmen brauchst du Schreibrechte auf diesem Match.",
          })}
        </p>
      ) : null}

      <div className="mt-4 empty:hidden">
        <AutoMatchRunsBox matchId={editor.matchId} latestSavedResultAt={editor.results[0]?.createdAt ?? null}
          onRematch={() => editor.setStepId("result")} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 @lg:grid-cols-2 @lg:h-[calc(100dvh-320px)] @lg:min-h-[480px]">
        <div className="box flex flex-col bg-surface p-5 @lg:min-h-0">
          <BoxHeader
            title={intl.formatMessage({
              id: "match.companyPool.finderTitle",
              defaultMessage: "Unternehmens-Finder",
            })}
            right={
              anyFilterActive ? (
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  {intl.formatMessage({ id: "common.reset", defaultMessage: "Reset" })}
                </Button>
              ) : null
            }
          />

          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={intl.formatMessage({
              id: "match.companyPool.searchPlaceholder",
              defaultMessage: "Name, Fachkompetenz, Branche …",
            })}
            aria-label={intl.formatMessage({
              id: "match.companyPool.searchLabel",
              defaultMessage: "Unternehmen suchen",
            })}
            className="w-full rounded bg-surface px-3 py-2 text-smycles-meta text-ink placeholder:text-ink-muted ring-1 ring-ink/10 focus:outline-none focus:ring-1 focus:ring-ink-ui"
          />

          <div className="mt-3">
            <MultiSelect
              options={industries.map((i) => ({ value: i.value, label: i.label }))}
              selected={industryIds}
              onChange={setIndustryIds}
              placeholder={intl.formatMessage({
                id: "match.companyPool.industryFilter",
                defaultMessage: "Branche vorfiltern …",
              })}
              searchable
            />
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="smycles-field-meta text-ink-muted">
                {intl.formatMessage({
                  id: "match.companyPool.industryFilterHint",
                  defaultMessage:
                    "Die Branche ist beides: ein Kriterium der Bewertung und ein Vorfilter für den Pool.",
                })}
              </span>
              {canTakeOverIndustries ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIndustryIds([...new Set([...industryIds, ...criteriaIndustryIds])])}
                >
                  {intl.formatMessage({
                    id: "match.companyPool.takeIndustriesFromCriteria",
                    defaultMessage: "Branchen aus den Kriterien übernehmen",
                  })}
                </Button>
              ) : null}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end">
            <Button
              variant="primary"
              size="compact"
              onClick={() => void addAllFound()}
              disabled={!canBulkAdd}
            >
              {search.hasMore
                ? // "All" would still be a lie while further pages exist: globalSearch exposes
                  // hasNextPage rather than a total, because counting would force a full scan. So
                  // the label promises the CEILING the click honours — the same rule and the same
                  // wording PeopleStep uses with match.people.addUpTo.
                  intl.formatMessage(
                    {
                      id: "match.companyPool.addUpTo",
                      defaultMessage: "Bis zu {count, number} aufnehmen",
                    },
                    // THE ROOM, not the absolute cap. The click adds at most `room`, so a pool
                    // holding 1,990 must not promise 2,000 — the sibling branch below has always
                    // been bounded this way and the two have to agree about what one click does.
                    { count: Math.min(MATCH_COMPANY_LIMIT, room) },
                  )
                : intl.formatMessage(
                    { id: "match.companyPool.addAllFound", defaultMessage: "Alle {count, number} aufnehmen" },
                    { count: Math.min(notInPool.length, room) },
                  )}
            </Button>
          </div>

          {/* pb-16: the sticky step navigation (MatchWorkflow) floats over the list's bottom edge. */}
          <div className="mt-3 max-h-[480px] overflow-auto p-1 pb-16 @lg:max-h-none @lg:min-h-0 @lg:flex-1">
            {found.length === 0 ? (
              <div className="bg-page box-flat py-12 text-center text-smycles-meta text-ink-muted">
                {search.loading
                  ? intl.formatMessage({ id: "common.loading", defaultMessage: "Laden…" })
                  : intl.formatMessage({
                      id: "match.companyPool.noneFound",
                      defaultMessage: "Keine Unternehmen gefunden",
                    })}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {found.map((hit) => {
                  const added = pool.byCompanyId.has(hit.id);
                  return (
                    <ResultCard
                      key={hit.id}
                      title={hit.name}
                      sub={null}
                      // The logo the search hydrated. A company without one — and one whose
                      // hydration has not landed — renders as the monogram.
                      avatar={hit.logo}
                      embedded
                      actions={
                        <Button
                          variant={added ? "chip" : "primary"}
                          size="sm"
                          onClick={() => void addOne(hit.id)}
                          disabled={!canEdit || added || pool.full}
                        >
                          {added
                            ? intl.formatMessage({
                                id: "match.companyPool.added",
                                defaultMessage: "Aufgenommen",
                              })
                            : intl.formatMessage({ id: "common.add", defaultMessage: "Add" })}
                        </Button>
                      }
                    />
                  );
                })}
              </div>
            )}
            {search.hasMore ? (
              <div className="mt-3 text-center">
                <Button variant="secondary" size="sm" onClick={() => search.loadMore()}>
                  {intl.formatMessage({ id: "common.loadMore", defaultMessage: "Load more" })}
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="box flex flex-col bg-surface p-5 @lg:min-h-0">
          <BoxHeader
            title={
              <span>
                {intl.formatMessage({
                  id: "match.companyPool.poolTitle",
                  defaultMessage: "Unternehmens-Pool",
                })}
                {/* THE SERVER'S COUNT, not the loaded rows': the read is capped, so at the cap
                    `rows.length` would stop being the size of the pool. It is also the number a
                    company match's `Match.candidateCount` reports — for this mode the candidates
                    ARE the companies. */}
                <span
                  className="ml-2 text-smycles-meta font-normal text-ink-muted tabular-nums"
                  data-testid="company-pool-count"
                >
                  · {pool.total}
                </span>
              </span>
            }
            right={
              pool.rows.length > 0 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void pool.removeAll()}
                  disabled={!canEdit}
                >
                  {intl.formatMessage({ id: "match.removeAll", defaultMessage: "Alle entfernen" })}
                </Button>
              ) : null
            }
          />

          {pool.refusal ? (
            // The one place a failed pool write speaks. Already localized by the hook — genesis's
            // own prose never reaches the screen (@adr SPARK-0010-no-raw-server-errors-in-ui@1) — and neutral, because the cap is a
            // rule of the platform and not a fault of the user.
            <div role="status" className="mb-3 rounded-md bg-ink/[0.06] px-3 py-2 text-smycles-meta text-ink">
              {pool.refusal}
            </div>
          ) : null}
          {pool.full ? (
            <div className="mb-3 rounded-md bg-ink/[0.06] px-3 py-2 text-smycles-meta text-ink-muted">
              {intl.formatMessage(
                {
                  id: "match.companyPool.fullNotice",
                  defaultMessage: "Der Pool ist mit {max, number} Unternehmen voll.",
                },
                { max: MATCH_COMPANY_LIMIT },
              )}
            </div>
          ) : null}
          {collectFailed ? (
            // Neutral and generic, like every other failure sentence here: genesis's own prose
            // never reaches the screen (@adr SPARK-0010-no-raw-server-errors-in-ui@1), and the
            // repair is simply to click again.
            <div role="status" className="mb-3 rounded-md bg-ink/[0.06] px-3 py-2 text-smycles-meta text-ink">
              {intl.formatMessage({
                id: "match.companyPool.collectFailed",
                defaultMessage:
                  "Die Treffer konnten nicht geladen werden. Bitte versuche es noch einmal.",
              })}
            </div>
          ) : null}
          {addNotice != null ? (
            <div className="mb-3 rounded-md bg-ink/[0.06] px-3 py-2 text-smycles-meta text-ink-muted">
              {addNotice.capped
                ? // THE SEARCH overflowed, not the pool: more companies match than one match may
                  // ever hold. Worth its own sentence, because "no more fit into the pool" would
                  // send someone to free up space that would not help.
                  intl.formatMessage(
                    {
                      id: "match.companyPool.addSearchCapped",
                      defaultMessage:
                        "Es wurden {count, number} Unternehmen aufgenommen – die Suche hat mehr Treffer, als ein Match führen kann ({max, number}).",
                    },
                    { count: addNotice.added, max: MATCH_COMPANY_LIMIT },
                  )
                : intl.formatMessage(
                    {
                      id: "match.companyPool.addCapped",
                      defaultMessage:
                        "Es wurden {count, number} Unternehmen aufgenommen – mehr passen nicht in den Pool.",
                    },
                    { count: addNotice.added },
                  )}
            </div>
          ) : null}

          {pool.loading ? (
            <div className="py-14 text-center text-smycles-meta text-ink-muted">
              {intl.formatMessage({ id: "common.loading", defaultMessage: "Laden…" })}
            </div>
          ) : pool.rows.length === 0 ? (
            <div className="py-14 text-center">
              <div className="mb-2 text-smycles-display-s text-ink-muted">◯</div>
              <div className="text-smycles-meta text-ink-muted">
                {intl.formatMessage({
                  id: "match.companyPool.empty",
                  defaultMessage:
                    "Noch keine Unternehmen – nimm links über Suche und Branche welche auf.",
                })}
              </div>
            </div>
          ) : (
            <div className="max-h-[480px] overflow-auto p-1 pb-16 @lg:max-h-none @lg:min-h-0 @lg:flex-1">
              <ul className="space-y-2">
                {pool.rows.map((row) => (
                  <PoolRowItem
                    key={row.id}
                    row={row}
                    industryLabel={industryLabel(row.industryId)}
                    logo={logoOf(row.companyId)}
                    onRemove={canEdit ? () => void pool.remove(row.id) : undefined}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// A pool row. The name is a real anchor to /c/:id — a company page exists at a stable URL, so it
// must survive middle-click — and the remove control sits BESIDE it rather than inside it: a button
// nested in an anchor is neither valid markup nor predictable with a keyboard.
function PoolRowItem({
  row,
  industryLabel,
  logo,
  onRemove,
}: {
  row: CompanyPoolRow;
  industryLabel: string;
  /** Null both while the hydration is in flight and when the company has none — both draw the monogram. */
  logo: CompanyLogoValue | null;
  onRemove?: () => void;
}) {
  const intl = useIntl();
  const meta = [
    industryLabel,
    row.employeeCount != null
      ? intl.formatMessage(
          { id: "match.companyPool.employeesValue", defaultMessage: "{count, number} Mitarbeitende" },
          { count: row.employeeCount },
        )
      : null,
    // Stated, never acted on: genesis keeps a dormant company in the pool and in the ranking
    // because the pool is the user's explicit choice (the company account model).
    row.inactive
      ? intl.formatMessage({ id: "match.companyPool.inactive", defaultMessage: "inaktiv" })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <li className="bg-page box-flat flex items-center gap-3 px-3 py-2">
      {/* The same rung and the same monogram fallback every other company row uses — the logo is an
          identity cue here, so a pool of pinned companies reads like the search results it was
          assembled from rather than a list of names. */}
      <CompanyLogo logo={logo} name={row.name} size="row" />
      <div className="min-w-0 flex-1">
        <Link
          to={`/c/${row.companyId}`}
          className="block truncate text-smycles-meta text-ink underline-offset-2 transition hover:text-ink-accent hover:underline"
        >
          {row.name}
        </Link>
        <div className="truncate text-smycles-label text-ink-muted">{meta}</div>
      </div>
      {onRemove ? (
        <Button
          variant="icon"
          size="sm"
          onClick={onRemove}
          aria-label={intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" })}
          title={intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" })}
          className="shrink-0"
        >
          <span className="text-smycles-body leading-none">✕</span>
        </Button>
      ) : null}
    </li>
  );
}
